/**
 * Generated by orval. Do not edit manually.
 */
import * as zod from 'zod'

/**
 * @summary List Feedback Forms
 */
export const listFeedbackFormsQuerySearchDefault = ''
export const listFeedbackFormsQuerySearchMax = 100

export const listFeedbackFormsQueryLimitDefault = 50
export const listFeedbackFormsQueryLimitMax = 100

export const listFeedbackFormsQueryOffsetDefault = 0
export const listFeedbackFormsQueryOffsetMin = 0

export const listFeedbackFormsQueryOrderingDefault = []

export const listFeedbackFormsQueryParams = zod.object({
  status: zod
    .union([zod.enum(['draft', 'live', 'paused', 'ended']), zod.null()])
    .optional()
    .describe(
      'Restrict to one effective status: a live form past its end date or at its response cap counts as ended. Omit for every status.',
    ),
  search: zod
    .string()
    .max(listFeedbackFormsQuerySearchMax)
    .optional()
    .describe('Case-insensitive substring match on the form name.'),
  limit: zod
    .number()
    .min(1)
    .max(listFeedbackFormsQueryLimitMax)
    .default(listFeedbackFormsQueryLimitDefault)
    .describe('Forms per page.'),
  offset: zod.number().min(listFeedbackFormsQueryOffsetMin).optional(),
  ordering: zod
    .array(
      zod.enum([
        'created_at',
        '-created_at',
        'updated_at',
        '-updated_at',
        'name',
        '-name',
        'priority',
        '-priority',
        'shown_count',
        '-shown_count',
        'answered_count',
        '-answered_count',
      ]),
    )
    .default(listFeedbackFormsQueryOrderingDefault)
    .describe(
      'Sort keys, applied in order; a `-` prefix sorts descending. Ties break on last edited, then id, so paging is stable. Defaults to last edited first.',
    ),
})

export const listFeedbackFormsResponse = zod.object({
  items: zod.array(
    zod.object({
      status: zod.enum(['draft', 'live', 'paused', 'ended']),
      effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
      shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
      answered_count: zod
        .number()
        .describe('Users who answered at least one question; previews excluded.'),
      answered_per_shown_percent: zod
        .union([zod.number(), zod.null()])
        .describe(
          'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
        ),
      id: zod.number(),
      name: zod.string(),
      template_key: zod.enum([
        'feature_rating',
        'new_feature_check_in',
        'flow_ease',
        'nps',
        'blank',
      ]),
      priority: zod.number(),
      start_date: zod.union([zod.iso.datetime({}), zod.null()]),
      end_date: zod.union([zod.iso.datetime({}), zod.null()]),
      current_version_number: zod
        .union([zod.number(), zod.null()])
        .describe('Number of the published version; null before.'),
      has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
      created_at: zod.iso.datetime({}),
      updated_at: zod.iso.datetime({}),
      updated_by_email: zod
        .union([zod.string(), zod.null()])
        .describe('Who edited the form last; null once that account is gone.'),
      headline: zod
        .union([
          zod.object({
            question_key: zod
              .string()
              .describe('The first closed question of the version; the headline reads it.'),
            question_type: zod.enum([
              'face_rating',
              'star_rating',
              'nps',
              'agreement',
              'yes_no',
              'single_choice',
              'multiple_choice',
              'open_text',
            ]),
            metric: zod.enum([
              'positive_share',
              'agree_share',
              'yes_share',
              'top_option_share',
              'mean',
              'nps',
            ]),
            label: zod
              .string()
              .describe(
                "What the value counts, for example 'Love it', 'Agree (5–7)', 'Average stars' or 'NPS'.",
              ),
            option_key: zod
              .union([zod.string(), zod.null()])
              .describe(
                'The option a share counts (face, yes, top answer); null for agree, mean and NPS.',
              ),
            estimate: zod.object({
              value: zod
                .union([zod.number(), zod.null()])
                .describe(
                  'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
                ),
              ci_low: zod
                .union([zod.number(), zod.null()])
                .describe('Lower bound of the 95% interval, same unit as value.'),
              ci_high: zod
                .union([zod.number(), zod.null()])
                .describe('Upper bound of the 95% interval, same unit as value.'),
              margin: zod
                .union([zod.number(), zod.null()])
                .describe(
                  'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
                ),
              n: zod.number().describe('How many answers the value is based on.'),
              is_small_sample: zod
                .boolean()
                .describe('True below 30 answers: show a small-sample note.'),
            }),
          }),
          zod.null(),
        ])
        .describe(
          "The current version's headline over every answer, unfiltered; null before the first publish or when the form has no closed question.",
        ),
    }),
  ),
  total: zod.number().describe('Total number of records'),
  limit: zod.number().describe('Limit of records in current page'),
  offset: zod.number().describe('Offset of current page. Records are ordered by date_created'),
})

/**
 * Create a draft form whose version 1 holds the template's questions.
 * @summary Create Feedback Form
 */
export const createFeedbackFormBodyNameMax = 100

export const createFeedbackFormBodyTemplateKeyDefault = 'blank'
export const createFeedbackFormBodyChangeReasonDefault = ''
export const createFeedbackFormBodyChangeReasonMax = 500

export const createFeedbackFormBody = zod.object({
  name: zod.string().min(1).max(createFeedbackFormBodyNameMax),
  template_key: zod
    .enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank'])
    .default(createFeedbackFormBodyTemplateKeyDefault)
    .describe('The template whose questions the draft starts with.'),
  change_reason: zod
    .string()
    .max(createFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

/**
 * @summary List Feedback Form Templates
 */
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMax = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMax = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxOne = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxOne = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMinMax = 60

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMaxMax = 60

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxTwo = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxTwo = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMinMaxOne = 60

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxThree = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxThree = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxFour = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxFour = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxFive = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxFive = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemLabelMax = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsMaxTwo = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOtherKeyMax = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOtherLabelMax = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxSix = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxSix = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsMaxThree = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxSeven = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxSeven = 300

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPlaceholderMaxOne = 200

export const listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsMax = 20

export const listFeedbackFormTemplatesResponse = zod.object({
  templates: zod
    .array(
      zod.object({
        key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
        name: zod.string(),
        description: zod.string(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMax,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExp,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMax,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxOne,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxTwo,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxThree,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxFour,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxFive,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxSix,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyMaxSeven,
                    )
                    .regex(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(listFeedbackFormTemplatesResponseTemplatesItemDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        locked_question_keys: zod
          .array(zod.string())
          .describe('Questions a form from this template cannot change.'),
      }),
    )
    .describe('Every template, in picker order.'),
})

/**
 * The plans the audience picker offers, in the same shape as GET /admin/v2/announcements/plans/.
 * @summary List Feedback Form Plans
 */
export const listFeedbackFormPlansResponse = zod.object({
  plans: zod
    .array(
      zod.object({
        id: zod.number().describe('The value audience_plans carries.'),
        tier: zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']),
        interval: zod.enum(['MONTHLY', 'YEARLY', 'DAILY']),
        variant: zod
          .union([zod.enum(['NLHOLDEM', 'PLO4', 'PLO5', 'PLO6']), zod.null()])
          .optional()
          .describe('Stored game variant; null on legacy plans.'),
        plan_type: zod.enum([
          'CASH',
          'SPINS',
          'HUSNG',
          'MTT',
          'SHORTDECK',
          'STRADDLE_ANTE_CASH',
          'ANY',
          'TOURNAMENT',
        ]),
        version: zod
          .number()
          .describe('Plan catalog version; several versions of one plan can be live at once.'),
        game: zod
          .enum(['HOLDEM', 'PLO'])
          .describe(
            'Display grouping for the admin plan matrix; targeting matches plan ids, never this.',
          ),
        plan_format: zod
          .enum(['CASH', 'TOURNAMENT', 'ANY'])
          .describe(
            'Display grouping for the admin plan matrix; targeting matches plan ids, never this.',
          ),
      }),
    )
    .describe(
      'Every plan audience_plans accepts: a paid tier (never FREE or TRIAL), a MONTHLY or YEARLY interval, and a plan_type with a plan_format (never SHORTDECK).',
    ),
})

/**
 * @summary Get Feedback Form
 */
export const getFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const getFeedbackFormResponseCurrentVersionDefinitionQuestionsMax = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const getFeedbackFormResponseDraftVersionDefinitionQuestionsMax = 20

export const getFeedbackFormResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax)
                    .regex(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax)
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax)
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          getFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(getFeedbackFormResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax)
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax)
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        getFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            getFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          getFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(getFeedbackFormResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * Audience, trigger, limits and thank-you fields apply in place. `definition` goes to the draft version: a published version never changes, and an unchanged definition creates no version.
 * @summary Update Feedback Form
 */
export const updateFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const updateFeedbackFormBodyNameDefault = ''
export const updateFeedbackFormBodyNameMax = 100

export const updateFeedbackFormBodyPriorityDefault = 0
export const updateFeedbackFormBodyPriorityMin = -32768
export const updateFeedbackFormBodyPriorityMax = 32767

export const updateFeedbackFormBodyTriggerTypeDefault = 'page'
export const updateFeedbackFormBodyTriggerEventNameDefault = ''
export const updateFeedbackFormBodyTriggerEventNameMax = 100

export const updateFeedbackFormBodyTriggerNthOccurrenceDefault = 1
export const updateFeedbackFormBodyTriggerNthOccurrenceMax = 1000

export const updateFeedbackFormBodyTriggerDelaySecondsDefault = 0
export const updateFeedbackFormBodyTriggerDelaySecondsMin = 0
export const updateFeedbackFormBodyTriggerDelaySecondsMax = 3600

export const updateFeedbackFormBodySamplePercentDefault = 100
export const updateFeedbackFormBodySamplePercentMax = 100

export const updateFeedbackFormBodyThankYouTextDefault = ''
export const updateFeedbackFormBodyThankYouTextMax = 300

export const updateFeedbackFormBodyThankYouLinkTextDefault = ''
export const updateFeedbackFormBodyThankYouLinkTextMax = 100

export const updateFeedbackFormBodyThankYouLinkUrlDefault = ''
export const updateFeedbackFormBodyThankYouLinkUrlMax = 500

export const updateFeedbackFormBodyAudienceDefault = 'all'
export const updateFeedbackFormBodyAudienceInvertedDefault = false
export const updateFeedbackFormBodyInsiderGateDefault = 'any'
export const updateFeedbackFormBodyMinAccountAgeDaysMinOne = 0
export const updateFeedbackFormBodyMinAccountAgeDaysMaxOne = 32767

export const updateFeedbackFormBodyMaxAccountAgeDaysMinOne = 0
export const updateFeedbackFormBodyMaxAccountAgeDaysMaxOne = 32767

export const updateFeedbackFormBodyRequiredFlagDefault = ''
export const updateFeedbackFormBodyRequiredFlagMax = 64

export const updateFeedbackFormBodyRequiredFlagInvertedDefault = false
export const updateFeedbackFormBodyLocalesItemMin = 2
export const updateFeedbackFormBodyLocalesItemMax = 16

export const updateFeedbackFormBodyLocalesMax = 50

export const updateFeedbackFormBodyLocalesInvertedDefault = false
export const updateFeedbackFormBodyRoutePrefixesItemMax = 200

export const updateFeedbackFormBodyRoutePrefixesItemRegExp = new RegExp('^')
export const updateFeedbackFormBodyRoutePrefixesMax = 50

export const updateFeedbackFormBodyRouteExcludePrefixesItemMax = 200

export const updateFeedbackFormBodyRouteExcludePrefixesItemRegExp = new RegExp('^')
export const updateFeedbackFormBodyRouteExcludePrefixesMax = 50

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMax = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMax = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions2ItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxOne = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxOne = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemLabelsMinMax = 60

export const updateFeedbackFormBodyDefinitionQuestionsItemLabelsMaxMax = 60

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxTwo = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxTwo = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemLabelsMinMaxOne = 60

export const updateFeedbackFormBodyDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxThree = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpThree = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxThree = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const updateFeedbackFormBodyDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxFour = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxFour = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxFive = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxFive = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemLabelMax = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsMaxTwo = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemOtherKeyMax = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOtherKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOtherLabelMax = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxSix = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxSix = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const updateFeedbackFormBodyDefinitionQuestionsItemOptionsMaxThree = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxSeven = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpSeven = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxSeven = 300

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const updateFeedbackFormBodyDefinitionQuestionsItemPlaceholderMaxOne = 200

export const updateFeedbackFormBodyDefinitionQuestionsMax = 20

export const updateFeedbackFormBodyChangeReasonDefault = ''
export const updateFeedbackFormBodyChangeReasonMax = 500

export const updateFeedbackFormBody = zod.object({
  name: zod.string().min(1).max(updateFeedbackFormBodyNameMax).optional(),
  priority: zod
    .number()
    .min(updateFeedbackFormBodyPriorityMin)
    .max(updateFeedbackFormBodyPriorityMax)
    .optional()
    .describe('Higher wins when two forms trigger.'),
  start_date: zod
    .union([zod.iso.datetime({}), zod.null()])
    .optional()
    .describe('Deliver from this instant. Send null to clear; omit to leave unchanged.'),
  end_date: zod
    .union([zod.iso.datetime({}), zod.null()])
    .optional()
    .describe('Deliver until this instant. Send null to clear; omit to leave unchanged.'),
  trigger_type: zod.enum(['page', 'event']).default(updateFeedbackFormBodyTriggerTypeDefault),
  trigger_event_name: zod
    .string()
    .max(updateFeedbackFormBodyTriggerEventNameMax)
    .optional()
    .describe('Required for an event trigger.'),
  trigger_nth_occurrence: zod
    .number()
    .min(1)
    .max(updateFeedbackFormBodyTriggerNthOccurrenceMax)
    .default(updateFeedbackFormBodyTriggerNthOccurrenceDefault),
  trigger_delay_seconds: zod
    .number()
    .min(updateFeedbackFormBodyTriggerDelaySecondsMin)
    .max(updateFeedbackFormBodyTriggerDelaySecondsMax)
    .optional(),
  sample_percent: zod
    .number()
    .min(1)
    .max(updateFeedbackFormBodySamplePercentMax)
    .default(updateFeedbackFormBodySamplePercentDefault),
  max_responses: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe('Response cap. Send null to clear; omit to leave unchanged.'),
  thank_you_text: zod.string().min(1).max(updateFeedbackFormBodyThankYouTextMax).optional(),
  thank_you_link_text: zod.string().max(updateFeedbackFormBodyThankYouLinkTextMax).optional(),
  thank_you_link_url: zod
    .string()
    .max(updateFeedbackFormBodyThankYouLinkUrlMax)
    .optional()
    .describe('In-app path starting with / or an http(s) URL; empty = no link.'),
  audience: zod
    .enum(['all', 'free', 'trial', 'paid'])
    .default(updateFeedbackFormBodyAudienceDefault),
  audience_plans: zod
    .array(zod.number())
    .optional()
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans.',
    ),
  audience_inverted: zod.boolean().optional(),
  insider_gate: zod
    .enum(['any', 'members', 'non_members'])
    .default(updateFeedbackFormBodyInsiderGateDefault),
  min_account_age_days: zod
    .union([
      zod
        .number()
        .min(updateFeedbackFormBodyMinAccountAgeDaysMinOne)
        .max(updateFeedbackFormBodyMinAccountAgeDaysMaxOne),
      zod.null(),
    ])
    .optional(),
  max_account_age_days: zod
    .union([
      zod
        .number()
        .min(updateFeedbackFormBodyMaxAccountAgeDaysMinOne)
        .max(updateFeedbackFormBodyMaxAccountAgeDaysMaxOne),
      zod.null(),
    ])
    .optional(),
  required_flag: zod.string().max(updateFeedbackFormBodyRequiredFlagMax).optional(),
  required_flag_inverted: zod.boolean().optional(),
  locales: zod
    .array(
      zod
        .string()
        .min(updateFeedbackFormBodyLocalesItemMin)
        .max(updateFeedbackFormBodyLocalesItemMax),
    )
    .max(updateFeedbackFormBodyLocalesMax)
    .optional(),
  locales_inverted: zod.boolean().optional(),
  route_prefixes: zod
    .array(
      zod
        .string()
        .min(1)
        .max(updateFeedbackFormBodyRoutePrefixesItemMax)
        .regex(updateFeedbackFormBodyRoutePrefixesItemRegExp),
    )
    .max(updateFeedbackFormBodyRoutePrefixesMax)
    .optional(),
  route_exclude_prefixes: zod
    .array(
      zod
        .string()
        .min(1)
        .max(updateFeedbackFormBodyRouteExcludePrefixesItemMax)
        .regex(updateFeedbackFormBodyRouteExcludePrefixesItemRegExp),
    )
    .max(updateFeedbackFormBodyRouteExcludePrefixesMax)
    .optional(),
  definition: zod
    .union([
      zod.object({
        questions: zod
          .array(
            zod.union([
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMax)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExp)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMax),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMax)
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExp,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMax)
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExp,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMax)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                options: zod
                  .tuple([
                    zod
                      .object({
                        key: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyMax)
                          .regex(updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyRegExp)
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemLabelMax),
                      })
                      .strict(),
                    zod
                      .object({
                        key: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyMax)
                          .regex(updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyRegExp)
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemLabelMax),
                      })
                      .strict(),
                    zod
                      .object({
                        key: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions2ItemKeyMax)
                          .regex(updateFeedbackFormBodyDefinitionQuestionsItemOptions2ItemKeyRegExp)
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions2ItemLabelMax),
                      })
                      .strict(),
                  ])
                  .describe(
                    'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                  ),
                type: zod.literal('face_rating'),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxOne)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpOne)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxOne),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxOne)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                labels: zod.object({
                  min: zod
                    .string()
                    .max(updateFeedbackFormBodyDefinitionQuestionsItemLabelsMinMax)
                    .describe('Text under the lowest point of the scale.'),
                  max: zod
                    .string()
                    .max(updateFeedbackFormBodyDefinitionQuestionsItemLabelsMaxMax)
                    .describe('Text under the highest point of the scale.'),
                }),
                type: zod.literal('star_rating'),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxTwo)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpTwo)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxTwo),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxTwo)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                labels: zod.object({
                  min: zod
                    .string()
                    .max(updateFeedbackFormBodyDefinitionQuestionsItemLabelsMinMaxOne)
                    .describe('Text under the lowest point of the scale.'),
                  max: zod
                    .string()
                    .max(updateFeedbackFormBodyDefinitionQuestionsItemLabelsMaxMaxOne)
                    .describe('Text under the highest point of the scale.'),
                }),
                type: zod.literal('nps'),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxThree)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpThree)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxThree),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxThree)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                labels: zod.object({
                  min: zod
                    .string()
                    .max(updateFeedbackFormBodyDefinitionQuestionsItemLabelsMinMaxTwo)
                    .describe('Text under the lowest point of the scale.'),
                  max: zod
                    .string()
                    .max(updateFeedbackFormBodyDefinitionQuestionsItemLabelsMaxMaxTwo)
                    .describe('Text under the highest point of the scale.'),
                }),
                type: zod.literal('agreement'),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxFour)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpFour)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxFour),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxFour)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                options: zod
                  .tuple([
                    zod
                      .object({
                        key: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyMaxOne)
                          .regex(
                            updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormBodyDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                          ),
                      })
                      .strict(),
                    zod
                      .object({
                        key: zod
                          .string()
                          .min(1)
                          .max(updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyMaxOne)
                          .regex(
                            updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormBodyDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                          ),
                      })
                      .strict(),
                  ])
                  .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                type: zod.literal('yes_no'),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxFive)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpFive)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxFive),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxFive)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                options: zod
                  .array(
                    zod.object({
                      key: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyMax)
                        .regex(updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyRegExp)
                        .describe(
                          'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                        ),
                      label: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemLabelMax),
                    }),
                  )
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemOptionsMaxTwo),
                type: zod.literal('single_choice'),
                other: zod
                  .union([
                    zod.object({
                      key: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemOtherKeyMax)
                        .regex(updateFeedbackFormBodyDefinitionQuestionsItemOtherKeyRegExp)
                        .describe(
                          'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                        ),
                      label: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemOtherLabelMax),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    "An extra last option that asks for the user's own text; null = no such option.",
                  ),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxSix)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpSix)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxSix),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxSix)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                options: zod
                  .array(
                    zod.object({
                      key: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyMaxOne)
                        .regex(updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemKeyRegExpOne)
                        .describe(
                          'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                        ),
                      label: zod
                        .string()
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemOptionsItemLabelMaxOne),
                    }),
                  )
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemOptionsMaxThree),
                type: zod.literal('multiple_choice'),
              }),
              zod.object({
                key: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemKeyMaxSeven)
                  .regex(updateFeedbackFormBodyDefinitionQuestionsItemKeyRegExpSeven)
                  .describe(
                    'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                  ),
                prompt: zod
                  .string()
                  .min(1)
                  .max(updateFeedbackFormBodyDefinitionQuestionsItemPromptMaxSeven),
                required: zod.boolean().describe('False shows a Skip button on the card.'),
                condition: zod
                  .union([
                    zod.object({
                      question_key: zod
                        .string()
                        .min(1)
                        .max(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                        )
                        .regex(
                          updateFeedbackFormBodyDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                        )
                        .describe('Key of an earlier question in the same form.'),
                      any_of: zod
                        .array(
                          zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                            )
                            .regex(
                              updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                            ),
                        )
                        .min(1)
                        .max(updateFeedbackFormBodyDefinitionQuestionsItemConditionAnyOfMaxSeven)
                        .describe(
                          "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                        ),
                    }),
                    zod.null(),
                  ])
                  .optional()
                  .describe(
                    'Show this question only after a matching answer to an earlier question; null = always.',
                  ),
                type: zod.literal('open_text'),
                placeholder: zod
                  .union([
                    zod
                      .string()
                      .max(updateFeedbackFormBodyDefinitionQuestionsItemPlaceholderMaxOne),
                    zod.null(),
                  ])
                  .optional()
                  .describe('Hint text inside the empty text box.'),
              }),
            ]),
          )
          .max(updateFeedbackFormBodyDefinitionQuestionsMax)
          .describe('Shown in this order, one per card step. The admin builder warns above three.'),
      }),
      zod.null(),
    ])
    .optional()
    .describe(
      'The full question list. It goes to the draft version (created when none exists); the published version never changes. Omit or send null to leave the questions alone.',
    ),
  change_reason: zod
    .string()
    .max(updateFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const updateFeedbackFormResponseCurrentVersionDefinitionQuestionsMax = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const updateFeedbackFormResponseDraftVersionDefinitionQuestionsMax = 20

export const updateFeedbackFormResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax)
                    .regex(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          updateFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(updateFeedbackFormResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          updateFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(updateFeedbackFormResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * Only a form that no user has seen can be deleted; end the form otherwise.
 * @summary Delete Feedback Form
 */
export const deleteFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const deleteFeedbackFormQueryChangeReasonDefault = ''
export const deleteFeedbackFormQueryChangeReasonMax = 500

export const deleteFeedbackFormQueryParams = zod.object({
  change_reason: zod
    .string()
    .max(deleteFeedbackFormQueryChangeReasonMax)
    .optional()
    .describe('Why the operator deleted it, shown in the admin log; blank = no reason recorded.'),
})

/**
 * Copy the form into a new draft with no answers, no schedule and its newest questions.
 * @summary Duplicate Feedback Form
 */
export const duplicateFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const duplicateFeedbackFormBodyChangeReasonDefault = ''
export const duplicateFeedbackFormBodyChangeReasonMax = 500

export const duplicateFeedbackFormBody = zod.object({
  change_reason: zod
    .string()
    .max(duplicateFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

/**
 * Freeze the draft as the current version. A draft form goes live; a paused form stays paused.
 * @summary Publish Feedback Form
 */
export const publishFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const publishFeedbackFormBodyChangeReasonDefault = ''
export const publishFeedbackFormBodyChangeReasonMax = 500

export const publishFeedbackFormBody = zod.object({
  change_reason: zod
    .string()
    .max(publishFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const publishFeedbackFormResponseCurrentVersionDefinitionQuestionsMax = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const publishFeedbackFormResponseDraftVersionDefinitionQuestionsMax = 20

export const publishFeedbackFormResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax)
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree,
                    )
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven,
                    )
                    .regex(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          publishFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(publishFeedbackFormResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          publishFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(publishFeedbackFormResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * @summary Pause Feedback Form
 */
export const pauseFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const pauseFeedbackFormBodyChangeReasonDefault = ''
export const pauseFeedbackFormBodyChangeReasonMax = 500

export const pauseFeedbackFormBody = zod.object({
  change_reason: zod
    .string()
    .max(pauseFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsMax = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const pauseFeedbackFormResponseDraftVersionDefinitionQuestionsMax = 20

export const pauseFeedbackFormResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax)
                    .regex(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(pauseFeedbackFormResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax)
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax)
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          pauseFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(pauseFeedbackFormResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * @summary Resume Feedback Form
 */
export const resumeFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const resumeFeedbackFormBodyChangeReasonDefault = ''
export const resumeFeedbackFormBodyChangeReasonMax = 500

export const resumeFeedbackFormBody = zod.object({
  change_reason: zod
    .string()
    .max(resumeFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsMax = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const resumeFeedbackFormResponseDraftVersionDefinitionQuestionsMax = 20

export const resumeFeedbackFormResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax)
                    .regex(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(resumeFeedbackFormResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          resumeFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(resumeFeedbackFormResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * End a live or paused form for good. Duplicate it to ask again.
 * @summary End Feedback Form
 */
export const endFeedbackFormParams = zod.object({
  form_id: zod.number(),
})

export const endFeedbackFormBodyChangeReasonDefault = ''
export const endFeedbackFormBodyChangeReasonMax = 500

export const endFeedbackFormBody = zod.object({
  change_reason: zod
    .string()
    .max(endFeedbackFormBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const endFeedbackFormResponseCurrentVersionDefinitionQuestionsMax = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const endFeedbackFormResponseDraftVersionDefinitionQuestionsMax = 20

export const endFeedbackFormResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMax)
                    .regex(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax)
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax)
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          endFeedbackFormResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(endFeedbackFormResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExp)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMax),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxOne)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxOne),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMax)
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax)
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxThree)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxThree),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        endFeedbackFormResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFour)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFour),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxFive)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxFive),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSix)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSix),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven)
                    .regex(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven)
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            endFeedbackFormResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          endFeedbackFormResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(endFeedbackFormResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * Show the draft to staff with the FEEDBACK_FORMS_PREVIEW flag. When no draft exists, a copy of the published version becomes the draft. Users keep answering the published version.
 * @summary Start Feedback Form Preview
 */
export const startFeedbackFormPreviewParams = zod.object({
  form_id: zod.number(),
})

export const startFeedbackFormPreviewBodyChangeReasonDefault = ''
export const startFeedbackFormPreviewBodyChangeReasonMax = 500

export const startFeedbackFormPreviewBody = zod.object({
  change_reason: zod
    .string()
    .max(startFeedbackFormPreviewBodyChangeReasonMax)
    .optional()
    .describe(
      'Why the operator made this change, shown in the admin log; blank = no reason recorded.',
    ),
})

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsMax = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsMax = 20

export const startFeedbackFormPreviewResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMax,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExp,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMax,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(startFeedbackFormPreviewResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExp,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMax,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxOne,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxThree,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxFour,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxFive,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxSix,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven,
                    )
                    .regex(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(startFeedbackFormPreviewResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * Delete the draft version and end its preview. The published version stays live.
 * @summary Discard Feedback Form Draft
 */
export const discardFeedbackFormDraftParams = zod.object({
  form_id: zod.number(),
})

export const discardFeedbackFormDraftQueryChangeReasonDefault = ''
export const discardFeedbackFormDraftQueryChangeReasonMax = 500

export const discardFeedbackFormDraftQueryParams = zod.object({
  change_reason: zod
    .string()
    .max(discardFeedbackFormDraftQueryChangeReasonMax)
    .optional()
    .describe('Why the operator discarded it, shown in the admin log; blank = no reason recorded.'),
})

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMax = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax = 60

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsMax = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMax = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxOne = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMinMax = 60

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax = 60

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne = 60

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne = 60

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxThree = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxThree = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo = 60

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo = 60

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxFour = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxFour = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxFive = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxFive = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOtherKeyMax = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOtherLabelMax = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxSix = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxSix = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne = 120

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven = 300

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven = 64

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven =
  new RegExp('^[a-z0-9]+(?:_[a-z0-9]+)*$')
export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven = 20

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne = 200

export const discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsMax = 20

export const discardFeedbackFormDraftResponse = zod.object({
  status: zod.enum(['draft', 'live', 'paused', 'ended']),
  effective_status: zod.enum(['draft', 'live', 'paused', 'ended']),
  shown_count: zod.number().describe('Users the card was shown to; staff previews excluded.'),
  answered_count: zod
    .number()
    .describe('Users who answered at least one question; previews excluded.'),
  answered_per_shown_percent: zod
    .union([zod.number(), zod.null()])
    .describe(
      'answered ÷ shown, in percentage points rounded to one decimal (62.5 == 62.5%); null while shown is 0.',
    ),
  id: zod.number(),
  name: zod.string(),
  template_key: zod.enum(['feature_rating', 'new_feature_check_in', 'flow_ease', 'nps', 'blank']),
  priority: zod.number(),
  start_date: zod.union([zod.iso.datetime({}), zod.null()]),
  end_date: zod.union([zod.iso.datetime({}), zod.null()]),
  current_version_number: zod
    .union([zod.number(), zod.null()])
    .describe('Number of the published version; null before.'),
  has_draft: zod.boolean().describe('True while a draft of the next version exists.'),
  created_at: zod.iso.datetime({}),
  updated_at: zod.iso.datetime({}),
  updated_by_email: zod
    .union([zod.string(), zod.null()])
    .describe('Who edited the form last; null once that account is gone.'),
  trigger_type: zod.enum(['page', 'event']),
  trigger_event_name: zod
    .string()
    .describe('GA event name an event trigger listens for; empty otherwise.'),
  trigger_nth_occurrence: zod
    .number()
    .describe('Show from this occurrence of the trigger on (1 = first).'),
  trigger_delay_seconds: zod.number().describe('Seconds the card waits after the trigger.'),
  sample_percent: zod.number().describe('Share of eligible users sampled, in percentage points.'),
  max_responses: zod
    .union([zod.number(), zod.null()])
    .describe('The form ends once this many users answered; null = no cap.'),
  thank_you_text: zod.string(),
  thank_you_link_text: zod.string(),
  thank_you_link_url: zod.string(),
  audience: zod.enum(['all', 'free', 'trial', 'paid']),
  audience_plans: zod
    .array(zod.number())
    .describe(
      'Ids of the subscription plans narrowing `audience`, from GET /plans/; empty = no plan filter. A viewer matches when one active subscription is on one of these plans. Sorted ascending.',
    ),
  audience_inverted: zod
    .boolean()
    .describe('Exclude viewers matching audience and audience_plans together.'),
  insider_gate: zod.enum(['any', 'members', 'non_members']),
  min_account_age_days: zod.union([zod.number(), zod.null()]),
  max_account_age_days: zod.union([zod.number(), zod.null()]),
  required_flag: zod
    .string()
    .describe('Backend feature flag that must be active; empty = no gate.'),
  required_flag_inverted: zod.boolean(),
  locales: zod
    .array(zod.string())
    .describe('Locale codes the FE shows the form in; empty = all locales.'),
  locales_inverted: zod.boolean(),
  route_prefixes: zod
    .array(zod.string())
    .describe('Show only on routes starting with one of these; empty = all.'),
  route_exclude_prefixes: zod
    .array(zod.string())
    .describe('Never show on routes starting with one of these.'),
  current_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMax,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExp,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMax,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxOne,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxTwo,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxThree,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxFour,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxFive,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxSix,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyMaxSeven,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(discardFeedbackFormDraftResponseCurrentVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('What users answer; null before.'),
  draft_version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        definition: zod.object({
          questions: zod
            .array(
              zod.union([
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMax)
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExp,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMax,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMax,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExp,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMax,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExp,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMax,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMax,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMax,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMax,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyMax,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions2ItemKeyRegExp,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions2ItemLabelMax,
                            ),
                        })
                        .strict(),
                    ])
                    .describe(
                      'Exactly three, ordered negative, neutral, positive: the faces follow that order.',
                    ),
                  type: zod.literal('face_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxOne,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpOne,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxOne,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxOne,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpOne,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxOne,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpOne,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxOne,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMinMax,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMaxMax,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('star_rating'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxTwo,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpTwo,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxTwo,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxTwo,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpTwo,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxTwo,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpTwo,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxTwo,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxOne,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxOne,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('nps'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxThree,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpThree,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxThree,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxThree,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpThree,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxThree,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpThree,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxThree,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  labels: zod.object({
                    min: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMinMaxTwo,
                      )
                      .describe('Text under the lowest point of the scale.'),
                    max: zod
                      .string()
                      .max(
                        discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemLabelsMaxMaxTwo,
                      )
                      .describe('Text under the highest point of the scale.'),
                  }),
                  type: zod.literal('agreement'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxFour,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpFour,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxFour,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFour,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFour,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFour,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFour,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFour,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .tuple([
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyMaxOne,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions0ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                      zod
                        .object({
                          key: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyMaxOne,
                            )
                            .regex(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemKeyRegExpOne,
                            )
                            .describe(
                              'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                            ),
                          label: zod
                            .string()
                            .min(1)
                            .max(
                              discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptions1ItemLabelMaxOne,
                            ),
                        })
                        .strict(),
                    ])
                    .describe("Exactly two: the 'yes' option first, then the 'no' option."),
                  type: zod.literal('yes_no'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxFive,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpFive,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxFive,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxFive,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpFive,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxFive,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpFive,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxFive,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMax,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMax,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsMaxTwo,
                    ),
                  type: zod.literal('single_choice'),
                  other: zod
                    .union([
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOtherKeyMax,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOtherKeyRegExp,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOtherLabelMax,
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      "An extra last option that asks for the user's own text; null = no such option.",
                    ),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxSix,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpSix,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxSix,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSix,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSix,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSix,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSix,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSix,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  options: zod
                    .array(
                      zod.object({
                        key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyMaxOne,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemKeyRegExpOne,
                          )
                          .describe(
                            'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                          ),
                        label: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsItemLabelMaxOne,
                          ),
                      }),
                    )
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemOptionsMaxThree,
                    ),
                  type: zod.literal('multiple_choice'),
                }),
                zod.object({
                  key: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyMaxSeven,
                    )
                    .regex(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemKeyRegExpSeven,
                    )
                    .describe(
                      'Stable key: lowercase letters, digits and single underscores. Keep it across versions only while the meaning stays the same, because the results group answers by key.',
                    ),
                  prompt: zod
                    .string()
                    .min(1)
                    .max(
                      discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPromptMaxSeven,
                    ),
                  required: zod.boolean().describe('False shows a Skip button on the card.'),
                  condition: zod
                    .union([
                      zod.object({
                        question_key: zod
                          .string()
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyMaxSeven,
                          )
                          .regex(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionQuestionKeyRegExpSeven,
                          )
                          .describe('Key of an earlier question in the same form.'),
                        any_of: zod
                          .array(
                            zod
                              .string()
                              .min(1)
                              .max(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemMaxSeven,
                              )
                              .regex(
                                discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfItemRegExpSeven,
                              ),
                          )
                          .min(1)
                          .max(
                            discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemConditionAnyOfMaxSeven,
                          )
                          .describe(
                            "Show the question only when the earlier answer is one of these. A choice answer matches on its option key; a scale answer matches on its value written as a string, for example '9'.",
                          ),
                      }),
                      zod.null(),
                    ])
                    .optional()
                    .describe(
                      'Show this question only after a matching answer to an earlier question; null = always.',
                    ),
                  type: zod.literal('open_text'),
                  placeholder: zod
                    .union([
                      zod
                        .string()
                        .max(
                          discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsItemPlaceholderMaxOne,
                        ),
                      zod.null(),
                    ])
                    .optional()
                    .describe('Hint text inside the empty text box.'),
                }),
              ]),
            )
            .max(discardFeedbackFormDraftResponseDraftVersionDefinitionQuestionsMax)
            .describe(
              'Shown in this order, one per card step. The admin builder warns above three.',
            ),
        }),
        published_at: zod
          .union([zod.iso.datetime({}), zod.null()])
          .describe('Null on a draft; set once, when it was published.'),
      }),
      zod.null(),
    ])
    .describe('Where question edits go; publishing it makes it the current version.'),
  preview_started_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .describe('Set while staff preview the draft in the app; null when no preview runs.'),
  locked_question_keys: zod
    .array(zod.string())
    .describe('Questions the template locks: they can be neither edited nor removed.'),
})

/**
 * Counts, the two rates the stat tiles show, and the headline with its 95% margin. Every results endpoint takes the same filters and excludes staff previews. Answers to different versions never mix: the version filter defaults to the current version.
 * @summary Get Feedback Form Results Summary
 */
export const getFeedbackFormResultsSummaryParams = zod.object({
  form_id: zod.number(),
})

export const getFeedbackFormResultsSummaryQueryLanguageMaxOne = 16

export const getFeedbackFormResultsSummaryQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for the current version. Answers to different versions never mix.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(getFeedbackFormResultsSummaryQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
})

export const getFeedbackFormResultsSummaryResponse = zod.object({
  version: zod
    .union([
      zod.object({
        id: zod.number(),
        number: zod.number(),
        published_at: zod.union([zod.iso.datetime({}), zod.null()]),
      }),
      zod.null(),
    ])
    .describe('The version read; null before a publish.'),
  versions: zod
    .array(
      zod.object({
        id: zod.number(),
        number: zod.number(),
        published_at: zod.union([zod.iso.datetime({}), zod.null()]),
      }),
    )
    .describe('Every published version, oldest first.'),
  counts: zod.object({
    shown: zod
      .number()
      .describe('Submissions: users the card was shown to. Staff previews never count.'),
    answered: zod.number().describe('Shown submissions with at least one answer.'),
    completed: zod.number().describe('Answered submissions that reached the last question.'),
    dismissed: zod.number().describe('Shown submissions closed with no answer.'),
    commented: zod.number().describe('Submissions with an open-text answer.'),
  }),
  answered_per_shown: zod.object({
    label: zod.string().describe("Names what the rate divides, for example 'answered ÷ shown'."),
    numerator: zod.number(),
    denominator: zod.number(),
    percent: zod
      .union([zod.number(), zod.null()])
      .describe('numerator ÷ denominator in percentage points; null at 0.'),
  }),
  commented_per_answered: zod.object({
    label: zod.string().describe("Names what the rate divides, for example 'answered ÷ shown'."),
    numerator: zod.number(),
    denominator: zod.number(),
    percent: zod
      .union([zod.number(), zod.null()])
      .describe('numerator ÷ denominator in percentage points; null at 0.'),
  }),
  headline: zod
    .union([
      zod.object({
        question_key: zod
          .string()
          .describe('The first closed question of the version; the headline reads it.'),
        question_type: zod.enum([
          'face_rating',
          'star_rating',
          'nps',
          'agreement',
          'yes_no',
          'single_choice',
          'multiple_choice',
          'open_text',
        ]),
        metric: zod.enum([
          'positive_share',
          'agree_share',
          'yes_share',
          'top_option_share',
          'mean',
          'nps',
        ]),
        label: zod
          .string()
          .describe(
            "What the value counts, for example 'Love it', 'Agree (5–7)', 'Average stars' or 'NPS'.",
          ),
        option_key: zod
          .union([zod.string(), zod.null()])
          .describe(
            'The option a share counts (face, yes, top answer); null for agree, mean and NPS.',
          ),
        estimate: zod.object({
          value: zod
            .union([zod.number(), zod.null()])
            .describe(
              'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
            ),
          ci_low: zod
            .union([zod.number(), zod.null()])
            .describe('Lower bound of the 95% interval, same unit as value.'),
          ci_high: zod
            .union([zod.number(), zod.null()])
            .describe('Upper bound of the 95% interval, same unit as value.'),
          margin: zod
            .union([zod.number(), zod.null()])
            .describe(
              'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
            ),
          n: zod.number().describe('How many answers the value is based on.'),
          is_small_sample: zod
            .boolean()
            .describe('True below 30 answers: show a small-sample note.'),
        }),
      }),
      zod.null(),
    ])
    .describe('Null when the version has no closed question.'),
})

/**
 * One aggregate per question of the version, shaped for the chart its type uses. Every results endpoint takes the same filters and excludes staff previews. Answers to different versions never mix: the version filter defaults to the current version.
 * @summary List Feedback Form Question Results
 */
export const listFeedbackFormQuestionResultsParams = zod.object({
  form_id: zod.number(),
})

export const listFeedbackFormQuestionResultsQueryLanguageMaxOne = 16

export const listFeedbackFormQuestionResultsQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for the current version. Answers to different versions never mix.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(listFeedbackFormQuestionResultsQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
})

export const listFeedbackFormQuestionResultsResponse = zod.object({
  version: zod.union([
    zod.object({
      id: zod.number(),
      number: zod.number(),
      published_at: zod.union([zod.iso.datetime({}), zod.null()]),
    }),
    zod.null(),
  ]),
  questions: zod
    .array(
      zod.object({
        key: zod.string(),
        type: zod.enum([
          'face_rating',
          'star_rating',
          'nps',
          'agreement',
          'yes_no',
          'single_choice',
          'multiple_choice',
          'open_text',
        ]),
        prompt: zod.string(),
        respondents: zod
          .number()
          .describe('Submissions that answered this question: the n of every percent here.'),
        options: zod
          .union([
            zod.array(
              zod.object({
                key: zod.string(),
                label: zod.string().describe('The label of the version read.'),
                count: zod.number(),
                percent: zod
                  .union([zod.number(), zod.null()])
                  .describe("count ÷ the question's respondents, in percentage points."),
                is_other: zod
                  .boolean()
                  .describe("True for the single-choice option that asks for the user's own text."),
              }),
            ),
            zod.null(),
          ])
          .describe(
            "Choice questions: every option in the order of the version, 'other' last. Null otherwise.",
          ),
        scale: zod
          .union([
            zod.array(
              zod.object({
                value: zod.number(),
                count: zod.number(),
                percent: zod
                  .union([zod.number(), zod.null()])
                  .describe("count ÷ the question's respondents, in percentage points."),
              }),
            ),
            zod.null(),
          ])
          .describe(
            'Stars, NPS and agreement: every point from low to high, zeros included. Null otherwise.',
          ),
        bands: zod
          .union([
            zod.array(
              zod.object({
                band: zod.enum(['negative', 'neutral', 'positive']),
                label: zod.string().describe("For example 'Detractors (0–6)' or the face label."),
                count: zod.number(),
                percent: zod
                  .union([zod.number(), zod.null()])
                  .describe("count ÷ the question's respondents, in percentage points."),
              }),
            ),
            zod.null(),
          ])
          .describe(
            'Faces, agreement and NPS: negative, neutral, positive, in that order. Null otherwise.',
          ),
        headline: zod
          .union([
            zod.object({
              question_key: zod
                .string()
                .describe('The first closed question of the version; the headline reads it.'),
              question_type: zod.enum([
                'face_rating',
                'star_rating',
                'nps',
                'agreement',
                'yes_no',
                'single_choice',
                'multiple_choice',
                'open_text',
              ]),
              metric: zod.enum([
                'positive_share',
                'agree_share',
                'yes_share',
                'top_option_share',
                'mean',
                'nps',
              ]),
              label: zod
                .string()
                .describe(
                  "What the value counts, for example 'Love it', 'Agree (5–7)', 'Average stars' or 'NPS'.",
                ),
              option_key: zod
                .union([zod.string(), zod.null()])
                .describe(
                  'The option a share counts (face, yes, top answer); null for agree, mean and NPS.',
                ),
              estimate: zod.object({
                value: zod
                  .union([zod.number(), zod.null()])
                  .describe(
                    'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
                  ),
                ci_low: zod
                  .union([zod.number(), zod.null()])
                  .describe('Lower bound of the 95% interval, same unit as value.'),
                ci_high: zod
                  .union([zod.number(), zod.null()])
                  .describe('Upper bound of the 95% interval, same unit as value.'),
                margin: zod
                  .union([zod.number(), zod.null()])
                  .describe(
                    'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
                  ),
                n: zod.number().describe('How many answers the value is based on.'),
                is_small_sample: zod
                  .boolean()
                  .describe('True below 30 answers: show a small-sample note.'),
              }),
            }),
            zod.null(),
          ])
          .describe('Null for open text.'),
        mean: zod
          .union([
            zod.object({
              value: zod
                .union([zod.number(), zod.null()])
                .describe(
                  'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
                ),
              ci_low: zod
                .union([zod.number(), zod.null()])
                .describe('Lower bound of the 95% interval, same unit as value.'),
              ci_high: zod
                .union([zod.number(), zod.null()])
                .describe('Upper bound of the 95% interval, same unit as value.'),
              margin: zod
                .union([zod.number(), zod.null()])
                .describe(
                  'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
                ),
              n: zod.number().describe('How many answers the value is based on.'),
              is_small_sample: zod
                .boolean()
                .describe('True below 30 answers: show a small-sample note.'),
            }),
            zod.null(),
          ])
          .describe('Stars and agreement only.'),
        percents_can_exceed_100: zod
          .boolean()
          .describe('True for multiple choice: one user picks several options.'),
      }),
    )
    .describe('In the order of the version.'),
})

/**
 * The headline metric per week (Monday, UTC), one series per version. Without a version filter it returns every published version, so the chart can break the line at each version boundary.
 * @summary Get Feedback Form Results Trend
 */
export const getFeedbackFormResultsTrendParams = zod.object({
  form_id: zod.number(),
})

export const getFeedbackFormResultsTrendQueryLanguageMaxOne = 16

export const getFeedbackFormResultsTrendQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for every published version, each its own series.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(getFeedbackFormResultsTrendQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
})

export const getFeedbackFormResultsTrendResponse = zod.object({
  points: zod
    .array(
      zod.object({
        x: zod
          .number()
          .describe(
            'Position on the x axis, increasing across versions; label it with week_start.',
          ),
        week_start: zod.iso.date().describe('Monday (UTC) of the week the submissions were shown.'),
        version_number: zod.number(),
        value: zod
          .union([zod.number(), zod.null()])
          .describe(
            'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
          ),
        ci_low: zod.union([zod.number(), zod.null()]),
        ci_high: zod.union([zod.number(), zod.null()]),
        n: zod.number(),
        is_small_sample: zod.boolean(),
      }),
    )
    .describe('Sorted by version, then week. Start a new line where version_number changes.'),
  versions: zod.array(
    zod.object({
      number: zod.number(),
      published_at: zod.union([zod.iso.datetime({}), zod.null()]),
      first_x: zod
        .union([zod.number(), zod.null()])
        .describe("x of the version's first point: draw the boundary marker here."),
      headline: zod
        .union([
          zod.object({
            question_key: zod
              .string()
              .describe('The first closed question of the version; the headline reads it.'),
            question_type: zod.enum([
              'face_rating',
              'star_rating',
              'nps',
              'agreement',
              'yes_no',
              'single_choice',
              'multiple_choice',
              'open_text',
            ]),
            metric: zod.enum([
              'positive_share',
              'agree_share',
              'yes_share',
              'top_option_share',
              'mean',
              'nps',
            ]),
            label: zod
              .string()
              .describe(
                "What the value counts, for example 'Love it', 'Agree (5–7)', 'Average stars' or 'NPS'.",
              ),
            option_key: zod
              .union([zod.string(), zod.null()])
              .describe(
                'The option a share counts (face, yes, top answer); null for agree, mean and NPS.',
              ),
            estimate: zod.object({
              value: zod
                .union([zod.number(), zod.null()])
                .describe(
                  'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
                ),
              ci_low: zod
                .union([zod.number(), zod.null()])
                .describe('Lower bound of the 95% interval, same unit as value.'),
              ci_high: zod
                .union([zod.number(), zod.null()])
                .describe('Upper bound of the 95% interval, same unit as value.'),
              margin: zod
                .union([zod.number(), zod.null()])
                .describe(
                  'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
                ),
              n: zod.number().describe('How many answers the value is based on.'),
              is_small_sample: zod
                .boolean()
                .describe('True below 30 answers: show a small-sample note.'),
            }),
          }),
          zod.null(),
        ])
        .describe(
          "The version's headline over the whole range; the line tracks its metric and option.",
        ),
    }),
  ),
})

/**
 * Shown, then answered, then wrote a comment. Every results endpoint takes the same filters and excludes staff previews. Answers to different versions never mix: the version filter defaults to the current version.
 * @summary Get Feedback Form Results Funnel
 */
export const getFeedbackFormResultsFunnelParams = zod.object({
  form_id: zod.number(),
})

export const getFeedbackFormResultsFunnelQueryLanguageMaxOne = 16

export const getFeedbackFormResultsFunnelQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for the current version. Answers to different versions never mix.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(getFeedbackFormResultsFunnelQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
})

export const getFeedbackFormResultsFunnelResponse = zod.object({
  steps: zod
    .array(
      zod.object({
        step: zod.enum(['shown', 'answered', 'commented']),
        label: zod.string(),
        count: zod.number(),
        percent_of_shown: zod.union([zod.number(), zod.null()]),
        percent_of_previous: zod
          .union([zod.number(), zod.null()])
          .describe('Null on the first step.'),
      }),
    )
    .describe('Shown, answered, wrote a comment.'),
})

/**
 * The headline metric per group of one snapshot attribute. A group with fewer than 5 answers comes back suppressed, with every number null. Every results endpoint takes the same filters and excludes staff previews. Answers to different versions never mix: the version filter defaults to the current version.
 * @summary Get Feedback Form Results Splits
 */
export const getFeedbackFormResultsSplitsParams = zod.object({
  form_id: zod.number(),
})

export const getFeedbackFormResultsSplitsQueryLanguageMaxOne = 16

export const getFeedbackFormResultsSplitsQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for the current version. Answers to different versions never mix.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(getFeedbackFormResultsSplitsQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
  by: zod.enum(['plan', 'account_age', 'language', 'device']),
})

export const getFeedbackFormResultsSplitsResponse = zod.object({
  dimension: zod.enum(['plan', 'account_age', 'language', 'device']),
  minimum_answers: zod
    .number()
    .describe('Groups with fewer answers than this are suppressed on the server.'),
  suppressed_group_count: zod.number(),
  headline: zod
    .union([
      zod.object({
        question_key: zod
          .string()
          .describe('The first closed question of the version; the headline reads it.'),
        question_type: zod.enum([
          'face_rating',
          'star_rating',
          'nps',
          'agreement',
          'yes_no',
          'single_choice',
          'multiple_choice',
          'open_text',
        ]),
        metric: zod.enum([
          'positive_share',
          'agree_share',
          'yes_share',
          'top_option_share',
          'mean',
          'nps',
        ]),
        label: zod
          .string()
          .describe(
            "What the value counts, for example 'Love it', 'Agree (5–7)', 'Average stars' or 'NPS'.",
          ),
        option_key: zod
          .union([zod.string(), zod.null()])
          .describe(
            'The option a share counts (face, yes, top answer); null for agree, mean and NPS.',
          ),
        estimate: zod.object({
          value: zod
            .union([zod.number(), zod.null()])
            .describe(
              'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
            ),
          ci_low: zod
            .union([zod.number(), zod.null()])
            .describe('Lower bound of the 95% interval, same unit as value.'),
          ci_high: zod
            .union([zod.number(), zod.null()])
            .describe('Upper bound of the 95% interval, same unit as value.'),
          margin: zod
            .union([zod.number(), zod.null()])
            .describe(
              'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
            ),
          n: zod.number().describe('How many answers the value is based on.'),
          is_small_sample: zod
            .boolean()
            .describe('True below 30 answers: show a small-sample note.'),
        }),
      }),
      zod.null(),
    ])
    .describe("The metric every group's headline measures."),
  groups: zod.array(
    zod.object({
      key: zod.string().describe("The stored value, or 'unknown' when the snapshot is empty."),
      label: zod.string(),
      is_suppressed: zod.boolean().describe('True below 5 answers: every number is null.'),
      shown: zod.union([zod.number(), zod.null()]),
      answered: zod.union([zod.number(), zod.null()]),
      answered_per_shown_percent: zod.union([zod.number(), zod.null()]),
      headline: zod
        .union([
          zod.object({
            value: zod
              .union([zod.number(), zod.null()])
              .describe(
                'The point value; null at n = 0. A share is in percentage points (62.5 == 62.5%), a mean is in scale points, NPS runs from -100 to 100.',
              ),
            ci_low: zod
              .union([zod.number(), zod.null()])
              .describe('Lower bound of the 95% interval, same unit as value.'),
            ci_high: zod
              .union([zod.number(), zod.null()])
              .describe('Upper bound of the 95% interval, same unit as value.'),
            margin: zod
              .union([zod.number(), zod.null()])
              .describe(
                'Half the width of the 95% interval, shown as ±margin; null below two answers or at n = 0.',
              ),
            n: zod.number().describe('How many answers the value is based on.'),
            is_small_sample: zod
              .boolean()
              .describe('True below 30 answers: show a small-sample note.'),
          }),
          zod.null(),
        ])
        .describe("The form headline's metric in this group; null when fewer than 5 answered."),
    }),
  ),
})

/**
 * Free-text answers with the snapshot attributes and the day. No item carries a name, an email or a user id. Every results endpoint takes the same filters and excludes staff previews. Answers to different versions never mix: the version filter defaults to the current version.
 * @summary List Feedback Form Comments
 */
export const listFeedbackFormCommentsParams = zod.object({
  form_id: zod.number(),
})

export const listFeedbackFormCommentsQueryLanguageMaxOne = 16

export const listFeedbackFormCommentsQueryQuestionKeyMaxOne = 64

export const listFeedbackFormCommentsQueryQuestionKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const listFeedbackFormCommentsQueryAnswerQuestionKeyMaxOne = 64

export const listFeedbackFormCommentsQueryAnswerQuestionKeyRegExpOne = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const listFeedbackFormCommentsQueryAnswerAnyOfItemMax = 64

export const listFeedbackFormCommentsQueryAnswerAnyOfItemRegExp = new RegExp(
  '^[a-z0-9]+(?:_[a-z0-9]+)*$',
)
export const listFeedbackFormCommentsQueryAnswerAnyOfDefault = []
export const listFeedbackFormCommentsQueryAnswerAnyOfMax = 20

export const listFeedbackFormCommentsQuerySearchDefault = ''
export const listFeedbackFormCommentsQuerySearchMax = 100

export const listFeedbackFormCommentsQueryOrderingDefault = '-answered_at'
export const listFeedbackFormCommentsQueryLimitDefault = 20
export const listFeedbackFormCommentsQueryLimitMax = 100

export const listFeedbackFormCommentsQueryOffsetDefault = 0
export const listFeedbackFormCommentsQueryOffsetMin = 0

export const listFeedbackFormCommentsQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for the current version. Answers to different versions never mix.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(listFeedbackFormCommentsQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
  question_key: zod
    .union([
      zod
        .string()
        .min(1)
        .max(listFeedbackFormCommentsQueryQuestionKeyMaxOne)
        .regex(listFeedbackFormCommentsQueryQuestionKeyRegExpOne),
      zod.null(),
    ])
    .optional()
    .describe(
      "An open-text question, or a single-choice question whose 'other' text to read. Omit for the first open-text question of the version.",
    ),
  answer_question_key: zod
    .union([
      zod
        .string()
        .min(1)
        .max(listFeedbackFormCommentsQueryAnswerQuestionKeyMaxOne)
        .regex(listFeedbackFormCommentsQueryAnswerQuestionKeyRegExpOne),
      zod.null(),
    ])
    .optional()
    .describe(
      'Keep comments whose submission answered this closed question with one of answer_any_of.',
    ),
  answer_any_of: zod
    .array(
      zod
        .string()
        .min(1)
        .max(listFeedbackFormCommentsQueryAnswerAnyOfItemMax)
        .regex(listFeedbackFormCommentsQueryAnswerAnyOfItemRegExp),
    )
    .max(listFeedbackFormCommentsQueryAnswerAnyOfMax)
    .default(listFeedbackFormCommentsQueryAnswerAnyOfDefault)
    .describe(
      "Option keys, or scale points written as strings ('9'); required with answer_question_key.",
    ),
  search: zod
    .string()
    .max(listFeedbackFormCommentsQuerySearchMax)
    .optional()
    .describe('Case-insensitive substring match on the comment.'),
  ordering: zod
    .enum(['answered_at', '-answered_at'])
    .default(listFeedbackFormCommentsQueryOrderingDefault),
  limit: zod
    .number()
    .min(1)
    .max(listFeedbackFormCommentsQueryLimitMax)
    .default(listFeedbackFormCommentsQueryLimitDefault)
    .describe('Comments per page.'),
  offset: zod.number().min(listFeedbackFormCommentsQueryOffsetMin).optional(),
})

export const listFeedbackFormCommentsResponse = zod.object({
  question_key: zod
    .union([zod.string(), zod.null()])
    .describe('The question read; null when the version has none.'),
  items: zod.array(
    zod.object({
      id: zod.number(),
      question_key: zod.string(),
      option_key: zod
        .union([zod.string(), zod.null()])
        .describe("Set on the 'other' text of a single-choice question."),
      text: zod.string(),
      answered_on: zod.iso
        .date()
        .describe('UTC day of the answer; no time, so a comment cannot be timed.'),
      plan_tier: zod.union([zod.string(), zod.null()]),
      account_age_band: zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']),
      language: zod
        .union([zod.string(), zod.null()])
        .describe("The app's UI language when the form was shown."),
      device: zod.union([zod.enum(['desktop', 'mobile']), zod.null()]),
    }),
  ),
  total: zod.number(),
  limit: zod.number(),
  offset: zod.number(),
})

/**
 * One CSV row per submission of the filtered version: the version, status, timestamps, snapshot attributes, and one column per question (a single choice with 'other' adds a '<key> (other)' column). No name, email or user id. Every text cell is formula-neutralised.

**Response (200):** a streamed raw response, not a JSON envelope.
- `Content-Type`: `text/csv; charset=utf-8`
- `Content-Disposition`: `attachment; filename="feedback-form-<id>-v<number>-<date>.csv"`
 * @summary Export Feedback Form Results
 */
export const exportFeedbackFormResultsParams = zod.object({
  form_id: zod.number(),
})

export const exportFeedbackFormResultsQueryLanguageMaxOne = 16

export const exportFeedbackFormResultsQueryParams = zod.object({
  version: zod
    .union([zod.number().min(1), zod.null()])
    .optional()
    .describe(
      'Published version number to read; omit for the current version. Answers to different versions never mix.',
    ),
  date_from: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or after this UTC day.'),
  date_to: zod
    .union([zod.iso.date(), zod.null()])
    .optional()
    .describe('Only submissions shown on or before this UTC day.'),
  plan_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe("The user's best plan tier when the form was shown."),
  account_age: zod
    .union([zod.enum(['under_3_months', '3_to_12_months', 'over_1_year', 'unknown']), zod.null()])
    .optional()
    .describe(
      'Account age when the form was shown: under 90 days, 90–364 days, or 365 days and more.',
    ),
  language: zod
    .union([zod.string().max(exportFeedbackFormResultsQueryLanguageMaxOne), zod.null()])
    .optional()
    .describe("The app's UI language when the form was shown, e.g. 'en'."),
  device: zod
    .union([zod.enum(['desktop', 'mobile']), zod.null()])
    .optional()
    .describe('The layout the card rendered in.'),
})
